import { useMemo } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { useAppClockService, useFeatureFlag } from '@mews/framework';
import { AdditionalServiceData, GeneralPricingMode, Service, useRepositoryQuery } from '@mews-commander/data-access';
import { Taxation } from '@mews-commander/types';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { FeatureFlag } from '@mews-commander/enums';
import { getTaxationsByTaxEnvironmentCode } from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { useSortReservationForLinking } from '@mews-commander/legacy-entities/src/utils/useSortReservationForLinking';
import { useCommanderSelector } from '@mews-commander/utils';

export const useDefaultFormValues = (service: Service, accountId: string) => {
    const appClockService = useAppClockService();
    const { taxEnvironmentCode, pricing } = useCurrentEnterprise();
    const taxations: Taxation[] = useCommanderSelector((state) =>
        getTaxationsByTaxEnvironmentCode(state, { taxEnvironmentCode })
    );
    const enterpriseId = useCurrentEnterpriseId();
    const isCompany = useCommanderSelector((state) => isAccountIdCompany(state, { accountId }));
    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);

    const { data } = useRepositoryQuery(
        'billing',
        'defaultReservationsForLinking',
        { enterpriseId, accountIds: [accountId], onlyUnpaidReservations: false },
        { enabled: newRoutingRulesPOSEnabled && !isCompany, refetchOnMount: 'always' }
    );

    const sortReservationForLinking = useSortReservationForLinking();
    const reservations = useMemo(
        () => (data || []).toSorted(sortReservationForLinking),
        [data, sortReservationForLinking]
    );

    const { basePrice } = service.data.value as AdditionalServiceData;

    const empty = {} as Record<string, string>;

    taxations.forEach((taxation) => {
        empty[taxation.code] = '';
    });

    const defaultTaxValues = (basePrice.taxValues || []).reduce((accumulator, taxValue) => {
        const taxation = taxations.find((tax) => tax.taxRateCodes.includes(taxValue.taxRateCode));

        if (taxation) {
            accumulator[taxation.code] = taxValue.taxRateCode;
        }

        return accumulator;
    }, empty);

    return {
        defaultStartUtc: appClockService.getAppNowUtc().value,
        defaultTaxValues,
        defaultAmount:
            (pricing === GeneralPricingMode.Net ? basePrice.netValue : basePrice.grossValue) || basePrice.value,
        ...(!isCompany && {
            defaultLinkedReservationId: reservations.length > 0 ? reservations[0].reservationId : undefined,
        }),
    };
};
